import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CalendarClock, CreditCard, RefreshCw, Repeat, XCircle } from 'lucide-react'
import {
  NavigationLink,
  MoneyRoundingNote,
  Button,
  Card,
  DetailHero,
  Chip,
  Drawer,
  DrawerTabPanel,
  DrawerTabs,
  EmptyState,
  ErrorState,
  FilterToolbar,
  LoadMore,
  KeyValueGrid,
  SearchField,
  Skeleton,
  StatusBadge,
} from '@shared/design-system/ui'
import { SubscriptionStatusSchema } from '@shared/contracts/enums'
import type { Currency, SubscriptionStatus } from '@shared/contracts/enums'
import { formatMoney } from '@shared/lib/format/currency'
import { Money } from '@shared/lib/money/money'
import { toast } from '@shared/lib/toast/toast-store'
import { useMediaQuery } from '@shared/lib/use-media-query'
import {
  buildSubscriptionChargeViews,
  cancelSubscription,
  retrySubscription,
  subscriptionChargeViewLabel,
  useSubscriptionInvoicesQuery,
  useSubscriptionQuery,
  useSubscriptionsInfiniteQuery,
} from '@entities/subscription'
import type { Subscription, SubscriptionInvoice } from '@entities/subscription'
import { getChargeStatusPresentation, saleIdLabel, useSaleDetailQuery } from '@entities/sale'
import { ExportButton } from '@shared/ui/ExportButton'
import type { ExportFormat } from '@shared/ui/ExportButton'
import { apiFetchBlob } from '@shared/api/http'
import { exportTimestamp } from '@shared/lib/export-timestamp'
import { triggerDownload } from '@shared/lib/download'
import {
  actions,
  alignRight,
  controls,
  contentCard,
  detail,
  header,
  headerText,
  hideOnMobile,
  invoiceAside,
  invoiceList,
  invoiceRow,
  mainCell,
  mobileCard,
  mobileCardHeader,
  mobileCardLead,
  mobileList,
  mobileMetaGrid,
  mobileMetaItem,
  mobileMetaLabel,
  mobileMetaValue,
  page,
  primary,
  resultCount,
  row,
  secondary,
  statusGroup,
  statusOption,
  subtitle,
  table,
  td,
  th,
  title,
} from './subscriptions.css'
import type { DateRangeValue } from '@shared/lib/date-range'
import { SubscriptionMetricsPanel } from './SubscriptionMetricsPanel'

const statusOptions = SubscriptionStatusSchema.options

function statusPresentation(status: SubscriptionStatus) {
  if (status === 'ACTIVE') return { label: 'Ativa', tone: 'success' as const }
  if (status === 'TRIALING') return { label: 'Trial', tone: 'informative' as const }
  if (status === 'PAST_DUE') return { label: 'Em atraso', tone: 'awaiting' as const }
  return { label: 'Cancelada', tone: 'closed' as const }
}

function subscriptionStatusPresentation(subscription: Subscription) {
  if (subscription.status !== 'CANCELED' && subscription.cancelAtPeriodEnd) {
    return {
      label: 'Cancela no fim',
      detail: `Ativa até ${formatDate(subscription.currentPeriodEndAt)}`,
      tone: 'awaiting' as const,
    }
  }

  return { ...statusPresentation(subscription.status), detail: undefined }
}

function invoiceStatusPresentation(status: SubscriptionInvoice['status']) {
  if (status === 'PAID') return { label: 'Paga', tone: 'success' as const }
  if (status === 'PENDING' || status === 'PROCESSING')
    return { label: 'Aberta', tone: 'awaiting' as const }
  if (status === 'AWAITING_ACTION') return { label: 'Aguardando ação', tone: 'awaiting' as const }
  if (status === 'BLOCKED') return { label: 'Em revisão', tone: 'awaiting' as const }
  if (status === 'PAST_DUE' || status === 'IN_GRACE')
    return { label: 'Atrasada', tone: 'awaiting' as const }
  return { label: 'Fechada', tone: 'closed' as const }
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(new Date(value))
}

function buyerLabel(subscription: Subscription) {
  return (
    subscription.buyerName ??
    subscription.buyerEmail ??
    subscription.buyerId ??
    'Comprador sem nome'
  )
}

function planLabel(subscription: Subscription) {
  return subscription.planName ?? subscription.planId
}

function viewerLabel(subscription: Subscription) {
  const role = subscription.viewerRole ?? 'PRODUCER'
  if (role === 'AFFILIATE') return 'Afiliado'
  if (role === 'COPRODUCER') return 'Coprodutor'
  return 'Produtor'
}

function viewerDetail(subscription: Subscription) {
  if (subscription.viewerCanManage !== false) return 'Gerencia cobranças e cancelamentos'
  return 'Somente leitura'
}

function gatewayLabel(subscription: Subscription) {
  if (subscription.gateway === 'STRIPE') return 'Processado pela VendePay'
  if (subscription.gateway === 'MERCADO_PAGO') return 'via Mercado Pago'
  if (subscription.gateway === 'PAYPAL') return 'via PayPal'
  return 'gateway externo'
}

function paymentMethodLabel(subscription: Subscription) {
  if (subscription.paymentMethod === 'CREDIT_CARD') return 'Cartão'
  if (subscription.paymentMethod === 'PIX') return 'PIX'
  if (subscription.paymentMethod === 'BOLETO') return 'Boleto'
  if (subscription.paymentMethod === 'PAYPAL') return 'PayPal'
  if (subscription.paymentMethod === 'APPLE_PAY') return 'Apple Pay'
  if (subscription.paymentMethod === 'GOOGLE_PAY') return 'Google Pay'
  return 'Amazon Pay'
}

function recurringAmounts(subscription: Subscription) {
  return {
    charge: subscription.chargeAmount,
    settlement: subscription.settlementAmount ?? subscription.recurringAmount,
  }
}

function sameAmount(left: Subscription['chargeAmount'], right: Subscription['settlementAmount']) {
  return left?.amount === right?.amount && left?.currency === right?.currency
}

function recurringCommercialLabel(subscription: Subscription) {
  const amount = subscription.commercialAmount
  return amount ? formatMoney(amount.amount, amount.currency) : 'Preço contratado não disponível'
}

function buyerChargeDetail(subscription: Subscription) {
  const { charge } = recurringAmounts(subscription)
  if (!charge || sameAmount(charge, subscription.commercialAmount)) return null
  return `Cobrança ao comprador ${formatMoney(charge.amount, charge.currency)}`
}

function nextBillingPresentation(subscription: Subscription) {
  if (subscription.status === 'CANCELED') {
    return { value: 'Sem próxima cobrança', detail: 'Cancelada' }
  }
  return {
    value: formatDate(subscription.currentPeriodEndAt),
    detail: subscription.cancelAtPeriodEnd ? 'Encerra no fim do período' : 'Renovação automática',
  }
}

function SubscriptionChargesList({
  subscription,
  invoices,
  onViewSale,
}: {
  subscription: Subscription
  invoices: SubscriptionInvoice[]
  onViewSale?: (saleId: string) => void
}) {
  const charges = buildSubscriptionChargeViews(subscription, invoices).filter(
    (charge) =>
      !charge.id.startsWith('initial:') && charge.saleId !== subscription.initialPaymentSaleId,
  )
  if (charges.length === 0) {
    return (
      <EmptyState
        icon={<CalendarClock size={22} />}
        title="Sem faturas"
        description="As renovações desta assinatura aparecem aqui."
      />
    )
  }

  return (
    <div className={invoiceList}>
      {charges.map((invoice) => {
        const presentation = invoiceStatusPresentation(invoice.status)
        const chargeAmount = invoice.chargeAmount ?? recurringAmounts(subscription).charge
        const variation =
          invoice.paidAmount?.currency === invoice.value.currency
            ? Money.fromMonetaryValue(invoice.paidAmount)
                .subtract(Money.fromMonetaryValue(invoice.value))
                .toMonetaryValue()
            : undefined
        return (
          <div key={invoice.id} className={invoiceRow}>
            <span className={mainCell}>
              <span className={primary}>{subscriptionChargeViewLabel(invoice)}</span>
              <span className={secondary}>
                {saleIdLabel(invoice.saleId)} · Vence {formatDate(invoice.dueAt)}
                {chargeAmount && !sameAmount(chargeAmount, invoice.value)
                  ? ` · cobrança ao comprador ${formatMoney(chargeAmount.amount, chargeAmount.currency)}`
                  : ''}
              </span>
              {subscription.productName ? (
                <span className={secondary}>{subscription.productName}</span>
              ) : null}
              {invoice.paidAmount ? (
                <span className={secondary}>
                  Preço contratado {formatMoney(invoice.value.amount, invoice.value.currency)}
                </span>
              ) : null}
              {variation ? (
                <span className={secondary}>
                  Variação cambial {formatMoney(variation.amount, variation.currency)}
                </span>
              ) : null}
              {invoice.paidAmount && variation ? (
                <MoneyRoundingNote total={invoice.paidAmount} parts={[invoice.value, variation]} />
              ) : null}
              {invoice.status !== 'PAID' && chargeAmount?.currency !== invoice.value.currency ? (
                <span className={secondary}>
                  Preço contratado. A liquidação depende do câmbio da cobrança.
                </span>
              ) : null}
            </span>
            <span className={primary}>
              {invoice.status === 'PAID'
                ? invoice.paidAmount
                  ? formatMoney(invoice.paidAmount.amount, invoice.paidAmount.currency)
                  : 'Liquidação em apuração'
                : formatMoney(invoice.value.amount, invoice.value.currency)}
            </span>
            <span className={invoiceAside}>
              <StatusBadge size="sm" tone={presentation.tone} label={presentation.label} />
              {onViewSale && invoice.status === 'PAID' ? (
                <NavigationLink
                  onClick={() => {
                    onViewSale(invoice.saleId)
                  }}
                >
                  {' '}
                  Ver venda
                </NavigationLink>
              ) : null}
            </span>
          </div>
        )
      })}
    </div>
  )
}

function InitialSubscriptionCharge({
  saleId,
  productName,
  onViewSale,
}: {
  saleId?: string
  productName?: string
  onViewSale?: (saleId: string) => void
}) {
  const purchase = useSaleDetailQuery(saleId || null, 'charge')

  if (!saleId) {
    return (
      <EmptyState
        icon={<CalendarClock size={22} />}
        title="Primeira cobrança indisponível"
        description="Não foi possível identificar a compra inicial desta assinatura."
      />
    )
  }
  if (purchase.isPending) return <Skeleton height={180} />
  if (purchase.isError) {
    return <ErrorState onRetry={() => void purchase.refetch()} />
  }

  const sale = purchase.data
  const status = getChargeStatusPresentation({
    status: sale.status,
    handledByGlobalChargeback: sale.handledByGlobalChargeback,
    financialEvents: sale.financialEvents,
  })
  return (
    <div role="group" aria-label="Primeira cobrança" className={detail}>
      <div className={invoiceList}>
        <div className={invoiceRow}>
          <span className={mainCell}>
            <span className={primary}>Primeira cobrança</span>
            <span className={secondary}>{saleIdLabel(sale.id)}</span>
            {productName ? <span className={secondary}>{productName}</span> : null}
            <span className={secondary}>Compra em {formatDate(sale.createdAt)}</span>
            {sale.commercialAmount ? (
              <span className={secondary}>
                Preço contratado{' '}
                {formatMoney(sale.commercialAmount.amount, sale.commercialAmount.currency)}
              </span>
            ) : null}
            {sale.fxVariation ? (
              <span className={secondary}>
                Variação cambial {formatMoney(sale.fxVariation.amount, sale.fxVariation.currency)}
              </span>
            ) : null}
            {sale.commercialAmount && sale.fxVariation ? (
              <MoneyRoundingNote
                total={sale.grossAmount}
                parts={[sale.commercialAmount, sale.fxVariation]}
              />
            ) : null}
          </span>
          <span className={mainCell}>
            <span className={secondary}>Total da compra</span>
            <span className={primary}>
              {formatMoney(sale.grossAmount.amount, sale.grossAmount.currency)}
            </span>
          </span>
          <StatusBadge size="sm" tone={status.tone} label={status.label} />
        </div>
      </div>
      <p className={secondary}>
        Uma compra pode incluir mais de uma assinatura. O total não representa sua comissão.
      </p>
      {onViewSale ? (
        <NavigationLink onClick={() => onViewSale(sale.id)}>Ver venda</NavigationLink>
      ) : null}
    </div>
  )
}

function SubscriptionDrawer({
  open,
  subscription,
  loading,
  error,
  onClose,
  onRetry,
  onViewSale,
}: {
  open: boolean
  subscription: Subscription | null
  loading?: boolean
  error?: boolean
  onClose: () => void
  onRetry?: () => void
  onViewSale?: (saleId: string) => void
}) {
  const [activeTab, setActiveTab] = useState('summary')
  const queryClient = useQueryClient()
  const invoices = useSubscriptionInvoicesQuery(subscription?.id ?? null)
  const cancel = useMutation({
    mutationFn: (subscriptionId: string) => cancelSubscription(subscriptionId, true),
    onSuccess: async () => {
      toast.success('Cancelamento agendado para o fim do período.')
      await queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
    },
    onError: () => {
      toast.error({
        title: 'Não foi possível agendar o cancelamento',
        description: 'Tente novamente.',
      })
    },
  })
  const retry = useMutation({
    mutationFn: retrySubscription,
    onSuccess: async () => {
      toast.success('Nova tentativa de cobrança solicitada.')
      await queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
    },
    onError: () => {
      toast.error({
        title: 'Não foi possível tentar a cobrança novamente',
        description: 'Tente novamente em alguns instantes.',
      })
    },
  })
  const status = subscription ? subscriptionStatusPresentation(subscription) : null
  const tabs = [
    { id: 'summary', label: 'Resumo' },
    { id: 'invoices', label: 'Cobranças' },
  ]

  function closeDrawer() {
    setActiveTab('summary')
    onClose()
  }

  return (
    <Drawer
      open={open}
      onClose={closeDrawer}
      title={subscription ? buyerLabel(subscription) : 'Assinatura'}
      description={
        subscription?.buyerEmail ?? 'Ciclo, cobranças, participantes e ações disponíveis.'
      }
      size="wide"
      variant="detail"
      busy={cancel.isPending || retry.isPending}
      status={status ? <StatusBadge tone={status.tone} label={status.label} /> : undefined}
      tabs={
        subscription ? (
          <DrawerTabs
            tabs={tabs}
            value={activeTab}
            onChange={setActiveTab}
            ariaLabel="Seções da assinatura"
          />
        ) : undefined
      }
    >
      {loading ? (
        <Skeleton height={280} />
      ) : error ? (
        <ErrorState onRetry={onRetry} />
      ) : subscription === null ? null : (
        <div className={detail}>
          <DrawerTabPanel id="summary" active={activeTab === 'summary'}>
            <DetailHero
              eyebrow="Preço contratado recorrente"
              value={recurringCommercialLabel(subscription)}
              description={`${viewerLabel(subscription)} · ${viewerDetail(subscription)}`}
              aside={
                subscription.status === 'CANCELED' ? undefined : (
                  <span>
                    Próximo ciclo
                    <br />
                    <strong>{formatDate(subscription.currentPeriodEndAt)}</strong>
                  </span>
                )
              }
            />
            <KeyValueGrid
              items={[
                {
                  label: 'Período atual',
                  value: `${formatDate(subscription.currentPeriodStartAt)} até ${formatDate(subscription.currentPeriodEndAt)}`,
                },
                { label: 'Plano', value: planLabel(subscription) },
                {
                  label: 'Situação',
                  value: [
                    subscriptionStatusPresentation(subscription).label,
                    subscriptionStatusPresentation(subscription).detail,
                  ]
                    .filter(Boolean)
                    .join(' · '),
                },
                {
                  label: 'Cobrança ao comprador',
                  value: subscription.chargeAmount
                    ? formatMoney(
                        subscription.chargeAmount.amount,
                        subscription.chargeAmount.currency,
                      )
                    : 'Cobrança histórica não disponível',
                },
              ]}
            />
            {subscription.viewerCanManage !== false && subscription.status === 'CANCELED' ? (
              <span className={secondary}>
                Assinatura encerrada. Não há novas cobranças ou ações disponíveis.
              </span>
            ) : subscription.viewerCanManage !== false ? (
              <div className={actions}>
                {subscription.status === 'PAST_DUE' ? (
                  <Button
                    variant="primary"
                    leftIcon={<RefreshCw size={16} />}
                    disabled={retry.isPending}
                    onClick={() => {
                      retry.mutate(subscription.id)
                    }}
                  >
                    Tentar cobrança
                  </Button>
                ) : null}
                <Button
                  variant="secondary"
                  leftIcon={<XCircle size={16} />}
                  disabled={subscription.cancelAtPeriodEnd || cancel.isPending}
                  onClick={() => {
                    cancel.mutate(subscription.id)
                  }}
                >
                  {subscription.cancelAtPeriodEnd
                    ? 'Cancelamento agendado'
                    : 'Cancelar no fim do período'}
                </Button>
              </div>
            ) : (
              <span className={secondary}>
                Somente leitura: cobranças e cancelamentos ficam com o produtor.
              </span>
            )}
          </DrawerTabPanel>

          <DrawerTabPanel id="invoices" active={activeTab === 'invoices'}>
            <InitialSubscriptionCharge
              saleId={subscription.initialPaymentSaleId}
              productName={subscription.productName}
              onViewSale={onViewSale}
            />
            {invoices.isPending ? (
              <Skeleton height={180} />
            ) : invoices.isError ? (
              <ErrorState
                onRetry={() => {
                  void invoices.refetch()
                }}
              />
            ) : (
              <SubscriptionChargesList
                subscription={subscription}
                invoices={invoices.data?.items ?? []}
                onViewSale={onViewSale}
              />
            )}
          </DrawerTabPanel>
        </div>
      )}
    </Drawer>
  )
}

interface SubscriptionsScreenProps {
  dateRange?: DateRangeValue
  onDateRangeChange?: (value: DateRangeValue) => void
  selectedId?: string | null
  currency?: Currency
  onCurrencyChange?: (currency: Currency) => void
  onSelect?: (subscriptionId: string | null) => void
  onViewSale?: (saleId: string) => void
}

export function SubscriptionsScreen({
  selectedId: controlledId,
  dateRange,
  onDateRangeChange,
  onSelect,
  onViewSale,
}: SubscriptionsScreenProps = {}) {
  const [status, setStatus] = useState<SubscriptionStatus | undefined>()
  const [query, setQuery] = useState('')
  const [internalId, setInternalId] = useState<string | null>(null)
  const isMobile = useMediaQuery('(max-width: 720px)')
  const [localRange, setLocalRange] = useState<DateRangeValue>({
    preset: 'month',
    from: null,
    to: null,
  })
  const selectedRange = dateRange ?? localRange
  const selectedCurrency = 'BRL'
  const subscriptions = useSubscriptionsInfiniteQuery(status, selectedCurrency, true)
  const historySearch = new URLSearchParams({
    currency: selectedCurrency,
    preset: selectedRange.preset,
  })
  if (selectedRange.preset === 'custom' && selectedRange.from && selectedRange.to) {
    historySearch.set('from', selectedRange.from)
    historySearch.set('to', selectedRange.to)
  }

  const selectedId = onSelect ? (controlledId ?? null) : internalId
  const setSelectedId = (id: string | null) => {
    if (onSelect) {
      onSelect(id)
    } else {
      setInternalId(id)
    }
  }

  const items = useMemo(
    () => subscriptions.data?.pages.flatMap((page) => page.items) ?? [],
    [subscriptions.data],
  )
  const visibleItems = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    if (!normalized) {
      return items
    }
    return items.filter((subscription) => {
      const haystack = [
        buyerLabel(subscription),
        subscription.buyerEmail,
        planLabel(subscription),
        paymentMethodLabel(subscription),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(normalized)
    })
  }, [items, query])
  async function handleExport(format: ExportFormat) {
    const qs = new URLSearchParams({ format, currency: selectedCurrency })
    if (status) qs.set('status', status)
    const blob = await apiFetchBlob(`/recurrence/subscriptions/export?${qs}`)
    triggerDownload(blob, `assinaturas-${exportTimestamp()}.${format}`)
  }

  const selectedFromList = items.find((subscription) => subscription.id === selectedId) ?? null
  const needsDetail = selectedId !== null && selectedFromList === null
  const selectedDetail = useSubscriptionQuery(needsDetail ? selectedId : null)
  const selected = selectedFromList ?? selectedDetail.data ?? null

  return (
    <div className={page} data-testid="subscriptions-screen">
      <div className={header}>
        <div className={headerText}>
          <h1 className={title}>Assinaturas</h1>
          <span className={subtitle}>
            Renovações, atrasos e participações das compras recorrentes.
          </span>
        </div>
        <ExportButton onExport={handleExport} />
      </div>

      <SubscriptionMetricsPanel
        dateRange={selectedRange}
        onDateRangeChange={(nextRange) => {
          setLocalRange(nextRange)
          onDateRangeChange?.(nextRange)
        }}
        currency={selectedCurrency}
      />

      <Card padding="md" className={contentCard}>
        <h2 className={primary}>Assinaturas das suas vendas</h2>
        <p className={secondary}>
          Acompanhe os produtos e as cobranças das vendas vinculadas a você. Os valores são brutos;
          sua comissão é apresentada no líquido de cada venda.
        </p>
        <NavigationLink href={`/vendas?${historySearch.toString()}`}>
          Consultar pagamentos em Vendas
        </NavigationLink>
        <p className={secondary}>
          Preços contratados em reais. A cobrança ao comprador pode usar outra moeda.
        </p>
        <FilterToolbar
          chips={
            status || query ? (
              <>
                {status ? (
                  <Chip
                    label={statusPresentation(status).label}
                    onRemove={() => setStatus(undefined)}
                  />
                ) : null}
                {query ? <Chip label={`Busca: ${query}`} onRemove={() => setQuery('')} /> : null}
              </>
            ) : null
          }
          onClear={
            status || query
              ? () => {
                  setStatus(undefined)
                  setQuery('')
                }
              : undefined
          }
          resultCount={
            <span className={resultCount}>
              {visibleItems.length}{' '}
              {visibleItems.length === 1 ? 'assinatura carregada' : 'assinaturas carregadas'}
            </span>
          }
        >
          <div className={controls}>
            <SearchField
              value={query}
              onChange={setQuery}
              placeholder="Buscar por comprador, e-mail ou plano"
              ariaLabel="Buscar assinaturas"
            />
            <div className={statusGroup} role="group" aria-label="Filtrar assinaturas por status">
              <button
                type="button"
                className={statusOption({ active: status === undefined })}
                aria-pressed={status === undefined}
                onClick={() => setStatus(undefined)}
              >
                Todas
              </button>
              {statusOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={statusOption({ active: status === option })}
                  aria-pressed={status === option}
                  onClick={() => setStatus(status === option ? undefined : option)}
                >
                  {statusPresentation(option).label}
                </button>
              ))}
            </div>
          </div>
        </FilterToolbar>

        {subscriptions.isPending ? (
          <Skeleton height={320} />
        ) : subscriptions.isError ? (
          <ErrorState
            onRetry={() => {
              void subscriptions.refetch()
            }}
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Repeat size={22} />}
            title="Nenhuma assinatura"
            description={`Nenhuma assinatura com sua participação em ${selectedCurrency} corresponde aos filtros atuais.`}
          />
        ) : visibleItems.length === 0 ? (
          <EmptyState
            icon={<Repeat size={22} />}
            title="Nada neste filtro"
            description="Nenhuma assinatura corresponde aos filtros atuais."
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  setStatus(undefined)
                  setQuery('')
                }}
              >
                Limpar filtros
              </Button>
            }
          />
        ) : (
          <>
            {isMobile ? (
              <div className={mobileList}>
                {visibleItems.map((subscription) => {
                  const presentation = subscriptionStatusPresentation(subscription)
                  const nextBilling = nextBillingPresentation(subscription)
                  return (
                    <div
                      key={subscription.id}
                      className={mobileCard}
                      role="button"
                      tabIndex={0}
                      aria-label={`Ver assinatura de ${buyerLabel(subscription)}`}
                      onClick={() => setSelectedId(subscription.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          setSelectedId(subscription.id)
                        }
                      }}
                    >
                      <div className={mobileCardHeader}>
                        <span className={mobileCardLead}>
                          <span className={primary}>{buyerLabel(subscription)}</span>
                          <span className={secondary}>
                            {subscription.buyerEmail ?? subscription.id}
                          </span>
                        </span>
                        <StatusBadge
                          tone={presentation.tone}
                          label={presentation.label}
                          size="sm"
                        />
                      </div>
                      <div className={mobileMetaGrid}>
                        <span className={mobileMetaItem}>
                          <span className={mobileMetaLabel}>Próxima cobrança</span>
                          <span className={mobileMetaValue}>{nextBilling.value}</span>
                          <span className={secondary}>{nextBilling.detail}</span>
                        </span>
                        <span className={mobileMetaItem}>
                          <span className={mobileMetaLabel}>Plano</span>
                          <span className={mobileMetaValue}>{planLabel(subscription)}</span>
                          <span className={secondary}>Valor bruto da assinatura</span>
                        </span>
                        <span className={mobileMetaItem}>
                          <span className={mobileMetaLabel}>Preço contratado</span>
                          <span className={mobileMetaValue}>
                            {recurringCommercialLabel(subscription)}
                          </span>
                          {buyerChargeDetail(subscription) ? (
                            <span className={secondary}>{buyerChargeDetail(subscription)}</span>
                          ) : null}
                        </span>
                        <span className={mobileMetaItem}>
                          <span className={mobileMetaLabel}>Pagamento</span>
                          <span className={mobileMetaValue}>
                            <CreditCard size={14} /> {paymentMethodLabel(subscription)}
                          </span>
                          <span className={secondary}>{gatewayLabel(subscription)}</span>
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <table className={table}>
                <thead>
                  <tr>
                    <th className={th}>Comprador</th>
                    <th className={th}>Status</th>
                    <th className={`${th} ${hideOnMobile}`}>Próxima cobrança</th>
                    <th className={`${th} ${hideOnMobile}`}>Valor recorrente</th>
                    <th className={`${th} ${hideOnMobile}`}>Pagamento</th>
                    <th className={`${th} ${alignRight}`}>Plano</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleItems.map((subscription) => {
                    const presentation = subscriptionStatusPresentation(subscription)
                    const nextBilling = nextBillingPresentation(subscription)
                    return (
                      <tr
                        key={subscription.id}
                        className={row}
                        onClick={() => setSelectedId(subscription.id)}
                      >
                        <td className={td}>
                          <span className={mainCell}>
                            <span className={primary}>{buyerLabel(subscription)}</span>
                            <span className={secondary}>
                              {subscription.buyerEmail ?? subscription.id}
                            </span>
                          </span>
                        </td>
                        <td className={td}>
                          <StatusBadge
                            tone={presentation.tone}
                            label={presentation.label}
                            size="sm"
                          />
                        </td>
                        <td className={`${td} ${hideOnMobile}`}>
                          <span className={mainCell}>
                            <span className={primary}>{nextBilling.value}</span>
                            <span className={secondary}>{nextBilling.detail}</span>
                          </span>
                        </td>
                        <td className={`${td} ${hideOnMobile}`}>
                          <span className={mainCell}>
                            <span className={primary}>
                              {recurringCommercialLabel(subscription)}
                            </span>
                            {buyerChargeDetail(subscription) ? (
                              <span className={secondary}>{buyerChargeDetail(subscription)}</span>
                            ) : null}
                          </span>
                        </td>
                        <td className={`${td} ${hideOnMobile}`}>
                          <span className={mainCell}>
                            <span className={primary}>
                              <CreditCard size={14} /> {paymentMethodLabel(subscription)}
                            </span>
                            <span className={secondary}>{gatewayLabel(subscription)}</span>
                          </span>
                        </td>
                        <td className={`${td} ${alignRight}`}>
                          <span className={mainCell}>
                            <span className={primary}>{planLabel(subscription)}</span>
                            <span className={secondary}>Valor bruto da assinatura</span>
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
            <LoadMore
              shown={visibleItems.length}
              hasMore={subscriptions.hasNextPage}
              loading={subscriptions.isFetchingNextPage}
              onClick={() => {
                void subscriptions.fetchNextPage()
              }}
            />
          </>
        )}
      </Card>

      <SubscriptionDrawer
        open={selectedId !== null}
        subscription={selected}
        loading={needsDetail && selectedDetail.isPending}
        error={needsDetail && selectedDetail.isError}
        onClose={() => setSelectedId(null)}
        onRetry={
          needsDetail
            ? () => {
                void selectedDetail.refetch()
              }
            : undefined
        }
        onViewSale={onViewSale}
      />
    </div>
  )
}
